<template>
  <div class="ev-simulate-page">
    <div class="page-header">
      <div class="header-main">
        <!-- 推导：默认收起，展开后占下一行 -->
        <div class="derive-toggle-wrap">
          <el-button size="mini" type="text" class="derive-toggle" :title="deriveExpanded ? '收起初始化' : '展开初始化'"
            @click="deriveExpanded = !deriveExpanded">
            <i :class="deriveExpanded ? 'el-icon-caret-bottom' : 'el-icon-caret-right'"></i>
            初始化
          </el-button>
          <el-button v-if="running && !deriveExpanded" type="danger" size="mini" :loading="stopping"
            @click="forceStopSimulate">强制停止</el-button>
        </div>

        <!-- 求解 EV 概要（居中） -->
        <div class="ev-block-wrap" v-if="hasSolveMetrics">
          <el-tooltip :content="evMetricTip" placement="bottom">
            <div class="ev-block">
              <div class="ev-metric is-penalty" :class="{ 'is-clickable': slackDetailAvailable }"
                @click.stop="openSlackDetail">
                <span class="ev-metric-label">缺票惩罚</span>
                <span class="ev-metric-value" :title="slackPenaltyTip">{{ formatEvNum(slackPenaltyValue) }}<span
                    v-if="slackSeatCount" class="ev-metric-unit">（{{ slackSeatCount }}座）</span></span>
              </div>
              <div class="ev-metric is-penalty">
                <span class="ev-metric-label">包车惩罚</span>
                <span class="ev-metric-value" :title="charterPenaltyTip">{{ formatEvNum(charterPenaltyValue) }}<span
                    v-if="charterShortCount" class="ev-metric-unit">（{{ charterShortCount }}辆）</span></span>
              </div>
              <div class="ev-metric is-penalty">
                <span class="ev-metric-label">缺口惩罚</span>
                <span class="ev-metric-value" :title="floorPenaltyTip">{{ formatEvNum(floorPenaltyValue) }}<span
                    v-if="floorShortCount" class="ev-metric-unit">（{{ floorShortCount }}辆）</span></span>
              </div>
              <div class="ev-metric is-penalty">
                <span class="ev-metric-label">D+1惩罚</span>
                <span class="ev-metric-value" :title="d1PenaltyTip">{{ formatEvNum(penaltyValue) }}</span>
              </div>
              <div class="ev-metric is-penalty">
                <span class="ev-metric-label">路线成本</span>
                <span class="ev-metric-value">{{ formatEvNum(tripPenaltyValue) }}</span>
              </div>
              <div class="ev-metric is-penalty">
                <span class="ev-metric-label">锁车惩罚</span>
                <span class="ev-metric-value" :title="lockPenaltyTip">{{ formatEvNum(lockPenaltyValue) }}</span>
              </div>
              <div class="ev-metric is-objective" :class="{ 'is-mismatch': evObjectiveMismatch }">
                <span class="ev-metric-label">目标值</span>
                <span class="ev-metric-value">{{ formatEvNum(objectiveValue) }}</span>
              </div>
              <div class="ev-metric" :class="solveQualityTone">
                <span class="ev-metric-label">求解质量</span>
                <span class="ev-metric-value" :title="solveQualityTip">
                  {{ solveQualityStatus }}
                  <span class="ev-metric-gap" v-if="solveQualityGap">{{ solveQualityGap }}</span>
                </span>
              </div>
              <el-button type="text" size="mini" class="ev-clear-result" :loading="clearingSolve"
                @click.stop="clearSolveResult">清除</el-button>
              <div class="ev-metric" :class="assignSolveTone" v-if="assignSolveVisible">
                <span class="ev-metric-label">派单质量</span>
                <span class="ev-metric-value" :title="assignSolveTip">
                  {{ assignSolveStatus }}
                  <span class="ev-metric-gap" v-if="assignSolveGap">{{ assignSolveGap }}</span>
                </span>
              </div>
              <div class="ev-metric is-cross-slot" v-if="crossSlotMarks.length">
                <span class="ev-metric-label">跨时段</span>
                <span class="ev-metric-value">
                  <span v-for="item in crossSlotOpenMarks" :key="item.id" class="ev-cross-slot-tag is-open"
                    :title="item.fullText" @click.stop="toggleCrossSlot(item)">{{ item.label }}</span>
                  <span v-if="crossSlotEnoughMarks.length === 1" class="ev-cross-slot-tag is-enough"
                    :title="crossSlotEnoughMarks[0].fullText" @click.stop="toggleCrossSlot(crossSlotEnoughMarks[0])">{{
                    crossSlotEnoughMarks[0].label }}</span>
                  <el-popover v-else-if="crossSlotEnoughMarks.length > 1" placement="bottom" trigger="click"
                    popper-class="ev-cross-slot-pop">
                    <div class="ev-cross-slot-menu">
                      <div v-for="item in crossSlotEnoughMarks" :key="item.id" class="ev-cross-slot-menu-item"
                        @click="toggleCrossSlot(item)">
                        <span>{{ item.name }}</span>
                        <span class="is-enough">车够</span>
                      </div>
                    </div>
                    <span slot="reference" class="ev-cross-slot-tag is-enough" @click.stop
                      :title="crossSlotEnoughTitle">{{ crossSlotEnoughMarks.length }}够</span>
                  </el-popover>
                </span>
              </div>
              <div class="ev-metric is-unassigned" v-if="unarrangedOrderCount > 0">
                <span class="ev-metric-label">未安排</span>
                <span class="ev-metric-value" :title="unarrangedTip">{{ unarrangedOrderCount }}单</span>
              </div>
              <div class="ev-metric is-reconcile" v-if="reconciledCount > 0 || reconcileFailedCount > 0">
                <span class="ev-metric-label">求解后补挂</span>
                <span class="ev-metric-value" :title="reconciledTip">{{ reconciledCount }}单</span>
              </div>
            </div>
          </el-tooltip>
        </div>
        <div class="ev-block-wrap" v-else>
          <div class="ev-block ev-placeholder">
            <span class="ev-placeholder-text">完成 EV 推导后在此显示求解概要</span>
          </div>
        </div>

        <!-- 查看筛选 -->
        <div class="header-block view-block">
          <div class="block-label">查看</div>
          <el-form size="mini" :inline="true" class="compact-form">
            <el-form-item label="路线">
              <el-select v-model="filterPathId" placeholder="全部路线" clearable style="width: 120px">
                <el-option v-for="item in pathOpts" :key="item.id" :label="item.name" :value="item.id"></el-option>
              </el-select>
            </el-form-item>
            <el-form-item label="日期">
              <el-date-picker v-model="viewDate" type="date" value-format="yyyy-MM-dd" placeholder="选择日期"
                style="width: 130px"></el-date-picker>
            </el-form-item>
            <el-form-item>
              <el-button size="mini" :loading="refreshing" @click="onRefresh">刷新</el-button>
            </el-form-item>
            <el-form-item>
              <el-button size="mini" plain :type="busyConflictTotal > 0 ? 'danger' : 'info'"
                @click="busyConflictVisible = true">系统繁忙 {{ busyConflictTotal }}</el-button>
            </el-form-item>
          </el-form>
        </div>

        <div v-show="deriveExpanded" class="header-block derive-block">
          <el-form size="mini" :inline="true" class="compact-form">
            <el-form-item label="开始日期">
              <el-date-picker v-model="param.startDate" type="date" value-format="yyyy-MM-dd" placeholder="选择日期"
                style="width: 130px" :disabled="running"></el-date-picker>
            </el-form-item>
            <el-form-item label="天数">
              <el-input-number v-model="param.days" :min="1" :max="7" :controls="false" style="width: 48px"
                :disabled="running"></el-input-number>
            </el-form-item>
            <el-form-item>
              <el-button type="primary" size="mini" :loading="running" @click="startSimulate">初始化</el-button>
              <el-button v-if="running" type="danger" size="mini" :loading="stopping"
                @click="forceStopSimulate">强制停止</el-button>
              <el-dropdown ref="moreDropdown" trigger="click" :hide-on-click="false" @command="onMoreCommand">
                <el-button size="mini" class="more-btn" :class="{ 'is-auto': opsTestAuto }">
                  更多<i class="el-icon-arrow-down el-icon--right"></i>
                </el-button>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item v-if="isDev" command="opsTest">操作测试</el-dropdown-item>
                  <el-dropdown-item v-if="isDev" command="noop" class="ops-auto-item">
                    <span>自动测试</span>
                    <el-switch v-model="opsTestAuto" @change="onOpsTestAutoChange" @click.native.stop></el-switch>
                  </el-dropdown-item>
                  <el-dropdown-item command="config">配置</el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </el-form-item>
          </el-form>
        </div>
      </div>

      <ev-epoch-dashboard :date="viewDate" :has-ledger="!!dayView" :ledger-status="solveStatus"
        :assigned-person="filteredAssignedPerson" :unassigned-person="filteredUnassignedPerson"
        :trip-count="filteredTripCount" :derive-at="solveExecutedAt" :derive-operator="solveOperator"
        :solve-time-text="solveTimeText" :has-extra="hasTaskStatus" :init-started-at="initStartedAt"
        :init-finished-at="initFinishedAt" @applied="onRefresh" @epoch-change="onEpochChange">
        <ev-init-day-wheel slot="wheel" :items="wheelItems" :active-date="viewDate" @select-date="onWheelSelectDate" />
        <div v-if="hasTaskStatus && task && task.status === 2" slot="extra" class="task-status">
          <span class="toolbar__error" :title="task.errorMsg" @click.stop="showTaskError">{{ task.errorMsg }}</span>
        </div>
      </ev-epoch-dashboard>
    </div>

    <div class="main-content">
      <div class="tabs-toolbar">
        <el-input v-model="searchTel" class="tel-search" size="mini" clearable placeholder="手机号搜索高亮"
          prefix-icon="el-icon-search" />
      </div>
      <el-tabs v-model="tab" class="fill-tabs">
        <el-tab-pane name="unassigned">
          <span slot="label">已订（{{ filteredUnassignedPerson }}人）</span>
          <unassigned-order ref="unassignedRef" :list="filteredUnassignedOrders" :date="viewDate"
            :table-height="tableMaxHeight" :highlight-tel="searchTel" :highlight-order-id="highlightOrderId"
            :unarranged-order-ids="unarrangedOrderIds" @assign="handleAssignOrder" />
        </el-tab-pane>

        <el-tab-pane name="dispatched">
          <span slot="label">已派（{{ filteredAssignedPerson }}人）</span>
          <simulated-dispatched-order ref="dispatchedRef" :trips="filteredDispatchedTrips"
            :table-height="tableMaxHeight" :highlight-tel="searchTel" :highlight-order-id="highlightOrderId"
            :reconciled-order-ids="reconciledOrderIds" @revoke="handleRevokeOrder" @reassign="handleReassignOrder" />
        </el-tab-pane>

        <el-tab-pane name="idle">
          <span slot="label">可售余票</span>
          <idle-vehicle-panel v-if="filterPathId" ref="idleRef" :rows="sellableSnapshots" :table-height="tableMaxHeight"
            :loading="refreshing" />
          <div v-else class="idle-empty-hint">请选择路线后点击刷新查看可售余票</div>
        </el-tab-pane>

        <el-tab-pane name="opAreaHour">
          <span slot="label">运营指标</span>
          <op-area-hour-panel v-if="filterPathId" :rows="opAreaHourSnapshots" :table-height="tableMaxHeight"
            :loading="refreshing" />
          <div v-else class="idle-empty-hint">请选择路线后点击刷新查看运营层数据</div>
        </el-tab-pane>

        <el-tab-pane name="vehicleInit">
          <span slot="label">日初状态（{{ vehicleInitCount }}人）</span>
          <vehicle-tip-panel :rows="vehicleInitRows" :view-date="viewDate" :table-height="tableMaxHeight"
            :loading="refreshing" />
        </el-tab-pane>

        <el-tab-pane name="vehicleFinal">
          <span slot="label">日末状态（{{ vehicleFinalCount }}人）</span>
          <vehicle-tip-panel :rows="vehicleFinalRows" :view-date="viewDate" :table-height="tableMaxHeight"
            :loading="refreshing" />
        </el-tab-pane>
      </el-tabs>
    </div>

    <assign-driver-dialog ref="assignDialog" @success="onAssignSuccess" />
    <ev-ops-test-dialog ref="opsTestDialog" @done="onOpsTestDone" />

    <el-dialog title="缺票明细" :visible.sync="slackDetailVisible" width="980px" append-to-body>
      <p class="slack-detail-tip">
        分派结束时没挂上的订单，按座位 ×（80×该路线票价）计入缺票惩罚。
        <template v-if="slackSeatCount || slackDetailRows.length">这次</template>
        <template v-if="slackSeatCount">缺 {{ slackSeatCount }} 座</template>
        <template v-if="slackSeatCount && slackDetailRows.length">，</template>
        <template v-if="slackDetailRows.length">共 {{ slackDetailRows.length }} 单</template>。
        后来补挂或改派成功的，现在在已派里。
      </p>
      <el-table :data="slackDetailRows" size="mini" max-height="420" empty-text="这次只记下了金额，没有订单号">
        <el-table-column label="订单" width="120">
          <template slot-scope="scope">
            <span class="pointer" @click="copySlackOrderId(scope.row.id)">{{ scope.row.id }}</span>
          </template>
        </el-table-column>
        <el-table-column label="路线" width="110">
          <template slot-scope="scope">{{ scope.row.pathId | pathNameTransfer }}</template>
        </el-table-column>
        <el-table-column label="时段" prop="showTime" width="110"></el-table-column>
        <el-table-column label="订票" width="60">
          <template slot-scope="scope">{{ scope.row.order | orderTypeTransfer }}</template>
        </el-table-column>
        <el-table-column label="手机" prop="telephone" width="120"></el-table-column>
        <el-table-column label="起点" prop="departure" min-width="140" show-overflow-tooltip></el-table-column>
        <el-table-column label="终点" prop="destination" min-width="140" show-overflow-tooltip></el-table-column>
        <el-table-column label="现状" prop="status" min-width="180"></el-table-column>
        <el-table-column label="" width="70">
          <template slot-scope="scope">
            <el-button v-if="scope.row.located" type="text" size="mini"
              @click="focusSlackOrder(scope.row)">定位</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-dialog>

    <el-dialog title="系统繁忙" :visible.sync="busyConflictVisible" width="920px" append-to-body>
      <p class="busy-conflict-tip">
        {{ viewDate }}<span v-if="filterPathName"> · {{ filterPathName }}</span>
        共 {{ busyConflictTotal }} 次。只计订票、改签把并发冲突重试耗尽、最终返回「系统繁忙」的次数，重试成功不计。
      </p>
      <div v-if="busyConflict.byPath && busyConflict.byPath.length" class="busy-conflict-paths">
        <el-tag v-for="row in busyConflict.byPath" :key="row.pathId || 'none'" size="mini" type="danger"
          effect="plain">{{
            row.pathName || ("路线" + row.pathId) }} {{ row.count }}</el-tag>
      </div>
      <el-table :data="busyConflict.events || []" size="mini" max-height="420" empty-text="这一天没有系统繁忙">
        <el-table-column type="expand">
          <template slot-scope="scope">
            <div class="busy-conflict-detail">
              <div v-if="scope.row.fromDate">改签前：{{ scope.row.fromDate }} {{ scope.row.fromPathName ||
                scope.row.fromPathId }} {{ scope.row.fromShowTime }}</div>
              <div>原因链：{{ scope.row.causeChain }}</div>
              <pre>{{ scope.row.stack }}</pre>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="时间" prop="occurredAt" width="150"></el-table-column>
        <el-table-column label="动作" width="110">
          <template slot-scope="scope">
            {{ scope.row.action }}<span v-if="scope.row.changeType"> · {{ scope.row.changeType }}</span>
          </template>
        </el-table-column>
        <el-table-column label="路线" min-width="120">
          <template slot-scope="scope">{{ scope.row.pathName || scope.row.pathId }}</template>
        </el-table-column>
        <el-table-column label="时段" prop="showTime" width="90"></el-table-column>
        <el-table-column label="订单" prop="orderId" width="90"></el-table-column>
        <el-table-column label="手机" prop="telephone" width="110"></el-table-column>
        <el-table-column label="人数" prop="number" width="50"></el-table-column>
        <el-table-column label="重试" prop="attempts" width="50"></el-table-column>
        <el-table-column label="冲突原因" prop="conflictMessage" min-width="160" show-overflow-tooltip></el-table-column>
      </el-table>
    </el-dialog>
  </div>
</template>

<script>
import {
  submitEvSimulate,
  getEvSimulateTask,
  getEvRunningSimulate,
  forceStopEvSimulate,
  getEvSolverHorizon,
  getEvPlanView,
  getEvSellableView,
  getEvOpAreaHourView,
  getEvSolveSummaryView,
  clearEvSolveResult,
  setEvCrossSlotMark,
  getEvVehicleFinalView,
  getEvVehicleInitView,
  getEvBusyConflict,
  revokeEvOrder
} from "@/api/ev_solver";
import { DateUtil, Toolkit } from "@/util";
import { mapState } from "vuex";
import config from "@/config/config";
import UnassignedOrder from "./components/simulate/UnassignedOrder";
import SimulatedDispatchedOrder from "./components/simulate/SimulatedDispatchedOrder";
import IdleVehiclePanel from "./components/simulate/IdleVehiclePanel";
import OpAreaHourPanel from "./components/simulate/OpAreaHourPanel";
import VehicleTipPanel from "./components/simulate/VehicleTipPanel";
import AssignDriverDialog from "./components/simulate/AssignDriverDialog";
import EvEpochDashboard from "./components/simulate/EvEpochDashboard";
import EvOpsTestDialog from "./components/simulate/EvOpsTestDialog";
import EvInitDayWheel from "./components/simulate/EvInitDayWheel";

const RUNNING_TASK_KEY = "evSimulate.runningTaskId";

/** 各页签数据缓存，切换页签/筛选条件时只读缓存，不自动请求 */
function createViewCache() {
  return {
    plan: {},
    sellable: {},
    opAreaHour: {},
    vehicleFinal: {},
    vehicleInit: {},
    solveSummary: {}
  };
}

export default {
  components: {
    UnassignedOrder,
    SimulatedDispatchedOrder,
    IdleVehiclePanel,
    OpAreaHourPanel,
    VehicleTipPanel,
    AssignDriverDialog,
    EvEpochDashboard,
    EvOpsTestDialog,
    EvInitDayWheel
  },

  data() {
    return {
      tab: "dispatched",
      searchTel: "",
      viewDate: DateUtil.getDayStr(0, "yyyy-MM-dd"),
      filterPathId: null,
      dayView: null,
      sellableSnapshots: [],
      opAreaHourSnapshots: [],
      solveSummary: null,
      clearingSolve: false,
      togglingCrossSlot: false,
      viewCache: createViewCache(),
      refreshing: false,
      lastLoadedQueryKey: null,
      param: {
        startDate: DateUtil.getDayStr(0, "yyyy-MM-dd"),
        days: 7
      },
      taskId: null,
      task: null,
      running: false,
      stopping: false,
      getResulted: true,
      pollTimer: null,
      horizonTimer: null,
      horizonItems: [],
      /** 当前查看日的 epoch 实时状态，轮盘与左侧「搜索中」共用 */
      liveEpochByDate: {},
      /** 各 tab 表格 max-height（px），按页签可视高度计算，只保留表体内部滚动 */
      tableMaxHeight: 480,
      deriveExpanded: false,
      opsTestAuto: false,
      opsTestAutoTimer: null,
      slackDetailVisible: false,
      highlightOrderId: null,
      busyConflictVisible: false,
      busySeq: 0,
      busyConflict: { total: 0, byPath: [], events: [] }
    };
  },

  computed: {
    ...mapState({
      pathOpts: (state) => state.pathList
    }),

    isDev() {
      return config.isDev;
    },

    pathCacheKey() {
      if (!this.viewDate || !this.filterPathId) {
        return null;
      }
      return `${this.viewDate}_${this.filterPathId}`;
    },

    filterPathName() {
      if (!this.filterPathId) {
        return "";
      }
      const hit = (this.pathOpts || []).find((item) => item.id === this.filterPathId);
      return hit ? hit.name : "";
    },

    busyConflictTotal() {
      const total = this.busyConflict && this.busyConflict.total;
      return total == null ? 0 : total;
    },

    queryKey() {
      return `${this.viewDate || ""}_${this.filterPathId || ""}`;
    },

    solveStatus() {
      const summary = this.dayView && this.dayView.solveSummary;
      return (summary && summary.status) || "-";
    },

    penaltyValue() {
      const s = this.solveSummary;
      return s && s.penalty != null ? s.penalty : null;
    },

    slackPenaltyValue() {
      const s = this.solveSummary;
      return s && s.slackPenalty != null ? s.slackPenalty : null;
    },

    charterPenaltyValue() {
      const s = this.solveSummary;
      return s && s.charterPenalty != null ? s.charterPenalty : null;
    },

    floorPenaltyValue() {
      const s = this.solveSummary;
      return s && s.floorPenalty != null ? s.floorPenalty : null;
    },

    tripPenaltyValue() {
      const s = this.solveSummary;
      return s && s.tripCostTotal != null ? s.tripCostTotal : null;
    },

    tieBreakCostValue() {
      const s = this.solveSummary;
      return s && s.tieBreakCost != null ? s.tieBreakCost : 0;
    },

    lockPenaltyValue() {
      const s = this.solveSummary;
      return s && s.lockIdlePenalty != null ? s.lockIdlePenalty : 0;
    },

    objectiveValue() {
      const s = this.solveSummary;
      return s && s.objective != null ? s.objective : null;
    },

    /** 各项惩罚与成本相加后取相反数，与求解器目标一致 */
    reconciledObjectiveValue() {
      if (this.objectiveValue == null) {
        return null;
      }
      return 0
        - (this.slackPenaltyValue || 0)
        - (this.charterPenaltyValue || 0)
        - (this.floorPenaltyValue || 0)
        - (this.penaltyValue || 0)
        - (this.tripPenaltyValue || 0)
        - (this.lockPenaltyValue || 0)
        - (this.tieBreakCostValue || 0);
    },

    evObjectiveMismatch() {
      if (this.objectiveValue == null) {
        return false;
      }
      return Math.abs(this.reconciledObjectiveValue - this.objectiveValue) > 0.05;
    },

    evMetricTip() {
      const parts = ["目标值 = −缺票惩罚 − 包车惩罚 − 缺口惩罚 − D+1惩罚 − 路线成本 − 锁车惩罚 − 平局。D+1 是次日来不及放空去接、当晚车位盖不住的座位 ×（该线路缺票 ÷ 2），时间更晚所以比今天的缺票便宜。锁车是干等超过开进该区域那趟放空的行程时间后，比再放一辆更贵。平局是积分档和发车槽"];
      parts.push("缺票只计座位缺口，包车和库存缺口单独列出");
      if (this.solveQualityTip) {
        parts.push(this.solveQualityTip);
      }
      if (this.assignSolveTip) {
        parts.push(this.assignSolveTip);
      }
      if (this.evObjectiveMismatch) {
        parts.push("当前对不上：按展示项相加=" + this.formatEvNum(this.reconciledObjectiveValue)
          + "，求解器目标值=" + this.formatEvNum(this.objectiveValue));
      }
      if (this.crossSlotMetricText) {
        parts.push(this.crossSlotMetricText);
      }
      return parts.join("。");
    },

    slackSeatCount() {
      const s = this.solveSummary;
      const count = s && s.slackSeatCount;
      return count != null && count > 0 ? count : 0;
    },

    slackOrderIds() {
      const s = this.solveSummary;
      return (s && s.slackOrderIds) || [];
    },

    slackDetailAvailable() {
      return this.slackSeatCount > 0 || this.slackOrderIds.length > 0;
    },

    slackDetailRows() {
      const reconciled = {};
      this.reconciledOrderIds.forEach((id) => {
        if (id != null) {
          reconciled[String(id)] = true;
        }
      });
      const byId = {};
      ((this.dayView && this.dayView.unassignedOrders) || []).forEach((order) => {
        if (order && order.id != null) {
          byId[String(order.id)] = { order, tab: "unassigned", trip: null };
        }
      });
      ((this.dayView && this.dayView.dispatchedTrips) || []).forEach((trip) => {
        (trip.orderList || []).forEach((order) => {
          if (order && order.id != null) {
            byId[String(order.id)] = { order, tab: "dispatched", trip };
          }
        });
      });
      return this.slackOrderIds.filter((id) => id != null).map((id) => {
        const hit = byId[String(id)];
        const order = hit ? hit.order : { id };
        let status = "当前账本里没有这单";
        if (hit && hit.tab === "unassigned") {
          status = "仍在已订";
        } else if (hit) {
          const driver = hit.trip && hit.trip.driverNo ? "司机" + hit.trip.driverNo : "已派";
          const time = hit.trip && hit.trip.showTime ? hit.trip.showTime : "";
          status = "已挂到" + driver + (time ? " " + time : "");
          if (reconciled[String(id)]) {
            status = "求解后补挂，" + status;
          }
        }
        return {
          id: order.id,
          pathId: order.pathId,
          showTime: order.showTime || "",
          telephone: order.telephone || "",
          departure: order.departure || "",
          destination: order.destination || "",
          order,
          status,
          tab: hit ? hit.tab : "",
          located: !!hit
        };
      });
    },

    charterShortCount() {
      const s = this.solveSummary;
      const count = s && s.charterShortCount;
      return count != null && count > 0 ? count : 0;
    },

    floorShortCount() {
      const s = this.solveSummary;
      const count = s && s.floorShortCount;
      return count != null && count > 0 ? count : 0;
    },

    solverUnassignedOrderIds() {
      const s = this.solveSummary;
      return (s && s.solverUnassignedOrderIds) || [];
    },

    reconciledOrderIds() {
      const s = this.solveSummary;
      return (s && s.reconciledOrderIds) || [];
    },

    reconcileFailedOrderIds() {
      const s = this.solveSummary;
      return (s && s.reconcileFailedOrderIds) || [];
    },

    reconciledCount() {
      return this.reconciledOrderIds.length;
    },

    reconcileFailedCount() {
      return this.reconcileFailedOrderIds.length;
    },

    slackPenaltyTip() {
      const parts = ["各窗没被本窗或紧邻下一小时盖住的座位 ×（80×该路线票价）"];
      if (this.slackSeatCount) {
        parts.push("求解缺" + this.slackSeatCount + "座");
      }
      if (this.slackOrderIds.length) {
        parts.push("点击查看这" + this.slackOrderIds.length + "单");
      }
      if (this.slackPenaltyValue != null && this.charterPenaltyValue == null && this.floorPenaltyValue == null) {
        parts.push("这次结果是拆分前保存的，金额里还含包车和空窗缺班");
      }
      parts.push("缺票按座位计，与是否挂上班无关");
      return parts.join("；");
    },

    d1PenaltyTip() {
      return "次日早上放空赶不到的座位，按当晚留下的车折算。盖不住的每座只收该线路今天缺票的一半";
    },

    charterPenaltyTip() {
      const parts = ["包车没排出的车 × 满车座位 ×（80×该路线票价）"];
      if (this.charterShortCount) {
        parts.push("缺" + this.charterShortCount + "辆");
      }
      return parts.join("；");
    },

    lockPenaltyTip() {
      return "车已经到了还干等下一班。每个半点按开进该区域最便宜放空的成本除以那趟的半点数计。等得比开过来还久，就比再放一辆贵";
    },

    floorPenaltyTip() {
      const parts = ["每个半点、每条线：缺口 = max(0, I* − S_eff)，S_eff = 本档现存量 + 此前已收工，惩罚 = 该线行程成本 × 缺口车数"];
      if (this.floorShortCount) {
        parts.push("缺口" + this.floorShortCount + "辆");
      }
      return parts.join("；");
    },

    unarrangedOrderIds() {
      const currentIds = ((this.dayView && this.dayView.unassignedOrders) || [])
        .map((order) => (order && order.id != null ? order.id : null))
        .filter((id) => id != null);
      if (!this.solverUnassignedOrderIds.length) {
        return currentIds;
      }
      const current = {};
      currentIds.forEach((id) => {
        current[String(id)] = true;
      });
      return this.solverUnassignedOrderIds.filter((id) => id != null && current[String(id)]);
    },

    unarrangedOrderCount() {
      return this.unarrangedOrderIds.length;
    },

    unarrangedTip() {
      const parts = ["求解器没有安排到任何班次，仍在已订"];
      if (this.unarrangedOrderIds.length) {
        parts.push("订单：" + this.unarrangedOrderIds.join("、"));
      }
      return parts.join("。");
    },

    reconciledTip() {
      const parts = [];
      if (this.reconciledCount) {
        parts.push("补挂成功：" + this.reconciledOrderIds.join("、"));
      }
      if (this.reconcileFailedCount) {
        parts.push("仍未挂上：" + this.reconcileFailedOrderIds.join("、"));
      }
      return parts.join("；");
    },

    crossSlotMarks() {
      const summary = this.solveSummary;
      if (!summary) {
        return [];
      }
      const capable = summary.crossSlotCapablePathIds || [];
      if (!capable.length) {
        return [];
      }
      const opened = {};
      (summary.crossSlotPathIds || []).forEach((id) => {
        opened[id] = true;
      });
      const names = {};
      (this.pathOpts || []).forEach((item) => {
        names[item.id] = item.name;
      });
      let marks = capable.map((id) => ({
        id: id,
        name: names[id] || ("线路" + id),
        open: !!opened[id]
      }));
      if (this.filterPathId) {
        const selected = Number(this.filterPathId);
        marks = marks.filter((item) => Number(item.id) === selected);
      }
      const shorts = this.abbreviateCrossSlotNames(marks);
      const single = marks.length === 1;
      return marks.map((item, index) => ({
        id: item.id,
        name: item.name,
        open: item.open,
        label: single ? (item.open ? "已开" : "车够") : shorts[index] + (item.open ? " 开" : " 够"),
        fullText: item.name + (item.open ? " 已开跨时段，点击关闭" : " 车够，点击打开")
      }));
    },

    crossSlotOpenMarks() {
      return this.crossSlotMarks.filter((item) => item.open);
    },

    crossSlotEnoughMarks() {
      return this.crossSlotMarks.filter((item) => !item.open);
    },

    crossSlotEnoughTitle() {
      const names = this.crossSlotEnoughMarks.map((item) => item.name).join("、");
      return names ? names + " 车够，点击选择线路" : "";
    },

    crossSlotMetricText() {
      return this.crossSlotMarks.map((item) => item.fullText).join("；");
    },

    hasSolveMetrics() {
      return this.slackPenaltyValue != null
        || this.charterPenaltyValue != null
        || this.floorPenaltyValue != null
        || this.penaltyValue != null
        || this.tripPenaltyValue != null
        || this.objectiveValue != null;
    },

    solveQualityStatus() {
      const s = this.solveSummary;
      return (s && s.status) || "-";
    },

    solveQualityGap() {
      const s = this.solveSummary;
      return s ? this.formatGap(s.relativeGap) : "";
    },

    solveQualityText() {
      const status = this.solveQualityStatus;
      const gap = this.solveQualityGap;
      return gap ? `${status} ${gap}` : status;
    },

    solveQualityTone() {
      const status = this.solveSummary && this.solveSummary.status;
      if (status === "OPTIMAL") {
        return "is-ok";
      }
      if (status === "FEASIBLE") {
        return "is-warn";
      }
      return "";
    },

    orderAssign() {
      const s = this.solveSummary;
      return s && s.orderAssign ? s.orderAssign : null;
    },

    assignSolveVisible() {
      return !!this.orderAssign;
    },

    assignSolveStatus() {
      const status = this.orderAssign && this.orderAssign.status;
      return status || "-";
    },

    assignSolveGap() {
      const a = this.orderAssign;
      return a ? this.formatGap(a.relativeGap) : "";
    },

    assignSolveTone() {
      const status = this.orderAssign && this.orderAssign.status;
      if (status === "OPTIMAL") {
        return "is-ok";
      }
      if (status === "FEASIBLE" || status === "FAILED") {
        return "is-warn";
      }
      return "";
    },

    assignSolveTip() {
      const a = this.orderAssign;
      if (!a) {
        return "";
      }
      const parts = ["派单求解：每条路线单独限时，不改发车"];
      if (a.status === "OPTIMAL") {
        parts.push("OPTIMAL：各路线都已证明最优");
      } else if (a.status === "FEASIBLE") {
        parts.push("FEASIBLE：时限内只找到可行解，gap 大说明时间不够");
      } else if (a.status === "FAILED") {
        parts.push("FAILED：有路线在时限内没有可用解");
      }
      if (a.relativeGap != null && !Number.isNaN(Number(a.relativeGap))) {
        parts.push("最差相对gap=" + this.formatGap(a.relativeGap));
      }
      if (a.timeoutSeconds != null) {
        parts.push("每路线时限=" + a.timeoutSeconds + "s");
      }
      if (a.solveMs != null) {
        parts.push("最慢路线实耗=" + this.formatAssignMs(a.solveMs));
      }
      (a.paths || []).forEach((row) => {
        parts.push(this.assignPathLine(row));
      });
      return parts.join("，");
    },

    solveQualityTip() {
      const s = this.solveSummary;
      if (!s) {
        return "";
      }
      const parts = [];
      if (s.status === "OPTIMAL") {
        parts.push("OPTIMAL：已证明最优，放空若仍出现是模型在奖励它");
      } else if (s.status === "FEASIBLE") {
        parts.push("FEASIBLE：只是可行解，未证明最优，优先怀疑时间/gap");
      }
      if (s.relativeGap != null && !Number.isNaN(s.relativeGap)) {
        parts.push("相对gap=" + this.formatGap(s.relativeGap));
      }
      if (s.objectiveBound != null) {
        parts.push("上界=" + this.formatEvNum(s.objectiveBound));
      }
      if (s.timeoutSeconds != null) {
        parts.push("时限=" + s.timeoutSeconds + "s");
      }
      if (s.solveMs != null) {
        parts.push("实耗=" + this.solveTimeText);
      }
      return parts.join("，");
    },

    solveExecutedAt() {
      const s = this.solveSummary;
      return s && s.executedAt ? s.executedAt : null;
    },

    solveOperator() {
      const s = this.solveSummary;
      return s && s.operator ? s.operator : null;
    },

    solveTimeText() {
      const s = this.solveSummary;
      const ms = s && s.solveMs;
      if (ms == null || ms < 0) {
        return "";
      }
      if (ms < 1000) {
        return ms + "ms";
      }
      if (ms < 60000) {
        return (ms / 1000).toFixed(2) + "s";
      }
      const min = Math.floor(ms / 60000);
      const sec = ((ms % 60000) / 1000).toFixed(1);
      return min + "min " + sec + "s";
    },

    filteredUnassignedOrders() {
      const list = (this.dayView && this.dayView.unassignedOrders) || [];
      if (!this.filterPathId) return list;
      return list.filter((item) => item.pathId === this.filterPathId);
    },

    filteredDispatchedTrips() {
      const list = (this.dayView && this.dayView.dispatchedTrips) || [];
      if (!this.filterPathId) return list;
      return list.filter((item) => item.pathId === this.filterPathId);
    },

    filteredAssignedPerson() {
      return this.filteredDispatchedTrips.reduce((sum, trip) => sum + (trip.assignedSeats || 0), 0);
    },

    filteredUnassignedPerson() {
      return this.filteredUnassignedOrders.reduce((sum, item) => sum + (item.number || 0), 0);
    },

    filteredTripCount() {
      return this.filteredDispatchedTrips.length;
    },

    filterPathStartArea() {
      if (!this.filterPathId) {
        return null;
      }
      const path = (this.pathOpts || []).find((item) => item.id === this.filterPathId);
      return path && path.startArea != null ? path.startArea : null;
    },

    vehicleFinalRows() {
      return this.filterVehicleTipsByPath(this.viewCache.vehicleFinal[this.viewDate] || []);
    },

    vehicleInitRows() {
      return this.filterVehicleTipsByPath(this.viewCache.vehicleInit[this.viewDate] || []);
    },

    vehicleFinalCount() {
      return this.vehicleFinalRows.length;
    },

    vehicleInitCount() {
      return this.vehicleInitRows.length;
    },

    hasTaskStatus() {
      if (this.running) {
        return true;
      }
      return !!(this.task && (this.task.status === 0 || this.task.status === 2));
    },

    initStartedAt() {
      return this.task && this.task.ctime ? this.task.ctime : null;
    },

    initFinishedAt() {
      return this.task && this.task.completeTime ? this.task.completeTime : null;
    },

    showInitProgress() {
      return this.running || !!(this.task && this.task.status === 0);
    },

    horizonStart() {
      if (this.showInitProgress && this.task && this.task.startDate) {
        return this.task.startDate;
      }
      return DateUtil.getDayStr(0, "yyyy-MM-dd");
    },

    horizonDays() {
      if (this.showInitProgress && this.task && this.task.days) {
        return this.task.days;
      }
      return 7;
    },

    wheelItems() {
      const base = this.horizonItems.length ? this.horizonItems : this.buildPlaceholderHorizon();
      return base.map((item) => this.decorateWheelItem(item));
    }
  },

  watch: {
    viewDate() {
      // 日期查询始终拉最新，不读本地 viewCache
      if (!this.viewDate) {
        this.dayView = null;
        this.solveSummary = null;
        this.sellableSnapshots = [];
        this.opAreaHourSnapshots = [];
        return;
      }
      if (this.pathCacheKey) {
        this.$delete(this.viewCache.opAreaHour, this.pathCacheKey);
        this.$delete(this.viewCache.sellable, this.pathCacheKey);
      }
      this.$delete(this.viewCache.plan, this.viewDate);
      this.$delete(this.viewCache.vehicleFinal, this.viewDate);
      this.$delete(this.viewCache.vehicleInit, this.viewDate);
      this.$delete(this.viewCache.solveSummary, this.viewDate);
      this.lastLoadedQueryKey = null;
      this.syncFromCache();
      this.refreshAll();
    },
    filterPathId() {
      this.handleQueryChange(false);
    },
    tab() {
      this.$nextTick(() => this.updateTableMaxHeight());
    },
    horizonStart() {
      this.pollHorizon();
    }
  },

  mounted() {
    this.refreshAll();
    this.recoverRunningTask();
    this.startHorizonPoll();
    this.$nextTick(() => this.bindTableHeightObserver());
  },

  beforeDestroy() {
    this.stopPoll();
    this.stopHorizonPoll();
    this.stopOpsTestAuto();
    this.unbindTableHeightObserver();
  },

  methods: {
    openSlackDetail() {
      if (!this.slackDetailAvailable) {
        return;
      }
      this.slackDetailVisible = true;
    },

    copySlackOrderId(orderId) {
      if (!orderId) {
        return;
      }
      Toolkit.copyContentH5(String(orderId));
      this.$message.success("订单号已复制");
    },

    focusSlackOrder(row) {
      if (!row || !row.located) {
        return;
      }
      this.slackDetailVisible = false;
      if (row.pathId != null && this.filterPathId && Number(this.filterPathId) !== Number(row.pathId)) {
        this.filterPathId = row.pathId;
      }
      this.highlightOrderId = row.id;
      this.tab = row.tab || "dispatched";
      this.$nextTick(() => {
        this.$nextTick(() => {
          const ref = this.tab === "unassigned" ? this.$refs.unassignedRef : this.$refs.dispatchedRef;
          if (ref && ref.focusOrder) {
            ref.focusOrder(row.id);
          }
        });
      });
    },

    toggleCrossSlot(item) {
      if (!this.viewDate || !item || this.togglingCrossSlot) {
        return;
      }
      const date = this.viewDate;
      const open = !item.open;
      const name = item.name || ("线路" + item.id);
      const text = open
        ? "打开 " + name + " 在 " + date + " 的跨时段。下次求解允许这条线路跨时段。"
        : "关闭 " + name + " 在 " + date + " 的跨时段。下次求解按单窗。如果下次求解仍然判定这条线路缺座，会再自动打开。";
      this.$confirm(text, open ? "打开跨时段" : "关闭跨时段", {
        type: "warning",
        confirmButtonText: open ? "打开" : "关闭",
        cancelButtonText: "取消"
      }).then(() => {
        this.togglingCrossSlot = true;
        return setEvCrossSlotMark({ date, pathId: item.id, open });
      }).then((res) => {
        if (!res) {
          return null;
        }
        return getEvSolveSummaryView({ date, _hideLoading: true });
      }).then((res) => {
        if (!res) {
          return;
        }
        const summary = res.data || null;
        this.$set(this.viewCache.solveSummary, date, summary);
        if (this.viewDate === date) {
          this.solveSummary = summary;
        }
        this.$message.success((open ? "已打开 " : "已关闭 ") + name + " 的跨时段，下次求解生效");
      }).catch(() => { }).finally(() => {
        this.togglingCrossSlot = false;
      });
    },

    clearSolveResult() {
      if (!this.viewDate || this.clearingSolve) {
        return;
      }
      const date = this.viewDate;
      this.$confirm(
        "清除 " + date + " 已保存的求解概要和运营指标，并丢掉下次求解会接着用的旧解。已派行程保留。",
        "清除求解结果",
        { type: "warning", confirmButtonText: "清除", cancelButtonText: "取消" }
      ).then(() => {
        this.clearingSolve = true;
        return clearEvSolveResult({ date });
      }).then((res) => {
        if (!res) {
          return;
        }
        Object.keys(this.viewCache.opAreaHour).forEach((key) => {
          if (key.indexOf(date + "_") === 0) {
            this.$delete(this.viewCache.opAreaHour, key);
          }
        });
        this.$delete(this.viewCache.solveSummary, date);
        if (this.viewDate === date) {
          this.solveSummary = null;
          this.opAreaHourSnapshots = [];
        }
        this.$message.success("已清除 " + date + " 的求解结果");
      }).catch(() => { }).finally(() => {
        this.clearingSolve = false;
      });
    },

    abbreviateCrossSlotNames(marks) {
      const split = (name) => {
        const parts = String(name || "").split("→");
        if (parts.length !== 2) {
          return null;
        }
        return [parts[0].trim(), parts[1].trim()];
      };
      const build = (len) => marks.map((item) => {
        const seg = split(item.name);
        if (!seg) {
          const text = String(item.name || "");
          return text.length > 4 ? text.slice(0, 4) : text;
        }
        const cut = (text) => (text.length > len ? text.slice(0, len) : text);
        return cut(seg[0]) + "→" + cut(seg[1]);
      });
      const maxLen = marks.reduce((max, item) => {
        const seg = split(item.name);
        if (!seg) {
          return max;
        }
        return Math.max(max, seg[0].length, seg[1].length);
      }, 1);
      for (let len = 1; len <= maxLen; len++) {
        const labels = build(len);
        if (new Set(labels).size === labels.length) {
          return labels;
        }
      }
      return marks.map((item) => item.name);
    },

    formatEvNum(val) {
      if (val === null || val === undefined) {
        return "-";
      }
      return this.$options.filters.numFormat(val, 0);
    },

    formatGap(gap) {
      if (gap == null || Number.isNaN(Number(gap))) {
        return "";
      }
      return (Number(gap) * 100).toFixed(2) + "%";
    },

    formatAssignMs(ms) {
      if (ms == null || ms < 0) {
        return "";
      }
      if (ms < 1000) {
        return ms + "ms";
      }
      return (ms / 1000).toFixed(2) + "s";
    },

    assignPathLine(row) {
      if (!row) {
        return "";
      }
      const hit = (this.pathOpts || []).find((item) => item.id === row.pathId);
      const name = hit && hit.name ? hit.name : ("路线" + row.pathId);
      let line = name + " " + (row.status || "-");
      const gap = this.formatGap(row.relativeGap);
      if (gap) {
        line += " " + gap;
      }
      if (row.ms != null) {
        line += " " + this.formatAssignMs(row.ms);
      }
      return line;
    },

    bindTableHeightObserver() {
      const pane = this.getTabsContentEl();
      if (!pane || typeof ResizeObserver === "undefined") {
        this.updateTableMaxHeight();
        window.addEventListener("resize", this.updateTableMaxHeight);
        return;
      }
      this._tableHeightObserver = new ResizeObserver(() => {
        this.updateTableMaxHeight();
      });
      this._tableHeightObserver.observe(pane);
      this.updateTableMaxHeight();
    },

    unbindTableHeightObserver() {
      if (this._tableHeightObserver) {
        this._tableHeightObserver.disconnect();
        this._tableHeightObserver = null;
      }
      window.removeEventListener("resize", this.updateTableMaxHeight);
    },

    getTabsContentEl() {
      return this.$el && this.$el.querySelector(".el-tabs__content");
    },

    updateTableMaxHeight() {
      const pane = this.getTabsContentEl();
      if (!pane || !pane.clientHeight) {
        return;
      }
      const activePane = this.$el.querySelector(`#pane-${this.tab}`) || pane;
      const root = activePane.querySelector(".root-box");
      let chrome = 0;
      if (root) {
        const cs = window.getComputedStyle(root);
        chrome += (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
      }
      const footer = activePane.querySelector(".page");
      if (footer) {
        const fcs = window.getComputedStyle(footer);
        // display:none 的页签 offsetHeight 为 0，用 28 兜底
        chrome += Math.max(footer.offsetHeight, 28)
          + (parseFloat(fcs.marginTop) || 0)
          + (parseFloat(fcs.marginBottom) || 0);
      }
      this.tableMaxHeight = Math.max(160, Math.floor(pane.clientHeight - chrome));
    },

    showTaskError() {
      if (!this.task || !this.task.errorMsg) {
        return;
      }
      this.$alert(this.task.errorMsg, "初始化失败", {
        confirmButtonText: "关闭",
        customClass: "ev-task-error-alert"
      });
    },

    normalizeOpAreaHourView(data) {
      if (Array.isArray(data)) {
        return { snapshots: data };
      }
      if (!data) {
        return { snapshots: [] };
      }
      return { snapshots: data.snapshots || [] };
    },

    isQueryCached() {
      if (!this.viewDate) {
        return false;
      }
      if (!this.viewCache.plan[this.viewDate]) {
        return false;
      }
      if (!Object.prototype.hasOwnProperty.call(this.viewCache.solveSummary, this.viewDate)) {
        return false;
      }
      if (!Object.prototype.hasOwnProperty.call(this.viewCache.vehicleFinal, this.viewDate)) {
        return false;
      }
      if (!Object.prototype.hasOwnProperty.call(this.viewCache.vehicleInit, this.viewDate)) {
        return false;
      }
      if (!this.filterPathId) {
        return true;
      }
      const key = this.pathCacheKey;
      return key && Object.prototype.hasOwnProperty.call(this.viewCache.sellable, key)
        && Object.prototype.hasOwnProperty.call(this.viewCache.opAreaHour, key);
    },

    handleQueryChange(forcePathRefresh) {
      this.syncFromCache();
      const key = this.queryKey;
      if (!forcePathRefresh && key === this.lastLoadedQueryKey) {
        return;
      }
      if (!forcePathRefresh && this.isQueryCached()) {
        this.lastLoadedQueryKey = key;
        this.loadBusyConflict();
        return;
      }
      if (!this.viewDate) {
        return;
      }
      this.refreshAll();
    },

    invalidatePathViewCache() {
      if (this.pathCacheKey) {
        this.$delete(this.viewCache.opAreaHour, this.pathCacheKey);
        this.$delete(this.viewCache.sellable, this.pathCacheKey);
      }
      this.lastLoadedQueryKey = null;
    },

    filterVehicleTipsByPath(rows) {
      const list = rows || [];
      if (!this.filterPathId) {
        return list;
      }
      const startArea = this.filterPathStartArea;
      if (startArea == null) {
        return [];
      }
      return list.filter((row) => row && row.areaId === startArea);
    },

    syncFromCache() {
      this.dayView = this.viewCache.plan[this.viewDate] || null;
      this.solveSummary = this.viewCache.solveSummary[this.viewDate] || null;
      const key = this.pathCacheKey;
      this.sellableSnapshots = key ? (this.viewCache.sellable[key] || []) : [];
      const opCached = key ? this.viewCache.opAreaHour[key] : null;
      this.opAreaHourSnapshots = this.normalizeOpAreaHourView(opCached).snapshots;
    },

    onRefresh() {
      if (!this.viewDate) {
        this.$message.warning("请选择日期");
        return;
      }
      if (this.pathCacheKey) {
        this.$delete(this.viewCache.opAreaHour, this.pathCacheKey);
        this.$delete(this.viewCache.sellable, this.pathCacheKey);
      }
      this.$delete(this.viewCache.vehicleFinal, this.viewDate);
      this.$delete(this.viewCache.vehicleInit, this.viewDate);
      this.$delete(this.viewCache.solveSummary, this.viewDate);
      this.lastLoadedQueryKey = null;
      this.refreshAll();
    },

    loadBusyConflict() {
      if (!this.viewDate) {
        this.busyConflict = { total: 0, byPath: [], events: [] };
        return;
      }
      const date = this.viewDate;
      const pathId = this.filterPathId || null;
      const seq = this.busySeq + 1;
      this.busySeq = seq;
      const param = { date };
      if (pathId) {
        param.pathId = pathId;
      }
      getEvBusyConflict(param).then((res) => {
        if (this.busySeq !== seq || this.viewDate !== date || (this.filterPathId || null) !== pathId) {
          return;
        }
        const data = (res && res.data) || {};
        this.busyConflict = {
          total: data.total || 0,
          byPath: data.byPath || [],
          events: data.events || []
        };
      }).catch(() => { });
    },

    refreshAll() {
      if (!this.viewDate) {
        return Promise.resolve();
      }
      this.loadBusyConflict();
      this.refreshing = true;
      const planReq = getEvPlanView({ date: this.viewDate, _hideLoading: true });
      const summaryReq = getEvSolveSummaryView({ date: this.viewDate, _hideLoading: true });
      const vehicleFinalReq = getEvVehicleFinalView({ date: this.viewDate, _hideLoading: true });
      const vehicleInitReq = getEvVehicleInitView({ date: this.viewDate, _hideLoading: true });
      const pathKey = this.pathCacheKey;
      const pathReqs = pathKey
        ? [
          getEvSellableView({ date: this.viewDate, pathId: this.filterPathId, _hideLoading: true }),
          getEvOpAreaHourView({ date: this.viewDate, pathId: this.filterPathId, _hideLoading: true })
        ]
        : [];

      return Promise.all([planReq, summaryReq, vehicleFinalReq, vehicleInitReq, ...pathReqs])
        .then(([planRes, summaryRes, vehicleFinalRes, vehicleInitRes, ...pathResults]) => {
          this.$set(this.viewCache.plan, this.viewDate, planRes.data);
          this.dayView = planRes.data;
          const summary = summaryRes.data || null;
          this.$set(this.viewCache.solveSummary, this.viewDate, summary);
          this.solveSummary = summary;
          this.$set(this.viewCache.vehicleFinal, this.viewDate, vehicleFinalRes.data || []);
          this.$set(this.viewCache.vehicleInit, this.viewDate, vehicleInitRes.data || []);
          if (!pathKey) {
            this.sellableSnapshots = [];
            this.opAreaHourSnapshots = [];
            return;
          }
          const [sellableRes, opAreaHourRes] = pathResults;
          const sellable = sellableRes.data || [];
          const opView = this.normalizeOpAreaHourView(opAreaHourRes.data);
          this.$set(this.viewCache.sellable, pathKey, sellable);
          this.$set(this.viewCache.opAreaHour, pathKey, opView.snapshots);
          this.sellableSnapshots = sellable;
          this.opAreaHourSnapshots = opView.snapshots;
        })
        .finally(() => {
          this.refreshing = false;
          this.lastLoadedQueryKey = this.queryKey;
        });
    },

    startHorizonPoll() {
      this.stopHorizonPoll();
      this.pollHorizon();
      this.horizonTimer = setInterval(() => this.pollHorizon(), 3000);
    },

    stopHorizonPoll() {
      if (this.horizonTimer) {
        clearInterval(this.horizonTimer);
        this.horizonTimer = null;
      }
    },

    pollHorizon() {
      getEvSolverHorizon({
        startDate: this.horizonStart,
        days: this.horizonDays
      }).then((res) => {
        const data = (res && res.data) || {};
        this.horizonItems = (data.items || []).map((row) => ({
          date: this.normalizeHorizonDate(row && row.date),
          state: row.state || "idle",
          solverAlive: !!row.solverAlive,
          queued: !!row.queued,
          initInFlight: !!row.initInFlight,
          initHorizon: !!row.initHorizon
        }));
        this.horizonItems.forEach((row) => {
          if (!row.date) {
            return;
          }
          this.$set(this.liveEpochByDate, row.date, {
            solverAlive: row.solverAlive,
            queued: row.queued,
            initInFlight: row.initInFlight,
            initHorizon: row.initHorizon
          });
        });
      }).catch(() => { });
    },

    buildPlaceholderHorizon() {
      const start = this.horizonStart;
      const n = this.horizonDays || 7;
      const list = [];
      for (let i = 0; i < n; i++) {
        const date = this.addHorizonDays(start, i);
        if (date) {
          list.push({ date, state: "idle" });
        }
      }
      return list;
    },

    addHorizonDays(dateStr, n) {
      const raw = String(dateStr || "").replace(/-/g, "/");
      const d = new Date(raw);
      if (isNaN(d.getTime())) {
        return "";
      }
      d.setDate(d.getDate() + n);
      const m = d.getMonth() + 1;
      const day = d.getDate();
      return d.getFullYear() + "-" + (m < 10 ? "0" + m : m) + "-" + (day < 10 ? "0" + day : day);
    },

    normalizeHorizonDate(value) {
      if (value == null || value === "") {
        return "";
      }
      if (Array.isArray(value) && value.length >= 3) {
        const m = String(value[1]).padStart(2, "0");
        const d = String(value[2]).padStart(2, "0");
        return value[0] + "-" + m + "-" + d;
      }
      if (typeof value === "object") {
        const y = value.year;
        const m = value.monthValue || value.month;
        const d = value.dayOfMonth || value.day;
        if (y && m && d) {
          return y + "-" + String(m).padStart(2, "0") + "-" + String(d).padStart(2, "0");
        }
      }
      const s = String(value);
      const hit = s.match(/(\d{4})-(\d{2})-(\d{2})/);
      return hit ? hit[1] + "-" + hit[2] + "-" + hit[3] : s;
    },

    onEpochChange(doc) {
      const date = this.normalizeHorizonDate(doc && doc.date);
      if (!date) {
        return;
      }
      this.$set(this.liveEpochByDate, date, {
        solverAlive: !!doc.solverAlive,
        queued: !!doc.queued,
        initInFlight: !!doc.initInFlight,
        initHorizon: !!doc.initHorizon
      });
    },

    decorateWheelItem(item) {
      const date = this.normalizeHorizonDate(item && item.date);
      const live = this.liveEpochByDate[date] || {};
      const solverAlive = !!(item && item.solverAlive) || !!live.solverAlive;
      const queued = !!(item && item.queued) || !!live.queued;
      const initInFlight = !!(item && item.initInFlight) || !!live.initInFlight;
      const initHorizon = !!(item && item.initHorizon) || !!live.initHorizon;
      let state = (item && item.state) || "idle";
      if (initInFlight || solverAlive) {
        state = "running";
      } else if (queued) {
        state = "queued";
      } else if (initHorizon && (state === "idle" || state === "pending")) {
        state = "paused";
      }
      const next = Object.assign({}, item, {
        date,
        state,
        solverAlive,
        queued,
        initInFlight,
        initHorizon
      });
      if (this.showInitProgress && this.task) {
        return this.overlayInitState(next);
      }
      return next;
    },

    overlayInitState(item) {
      const date = item && item.date;
      if (!date || !this.task) {
        return item;
      }
      const inflight = this.task.inFlightDates || [];
      if (inflight.indexOf(date) >= 0) {
        return Object.assign({}, item, { state: "running" });
      }
      if (item.queued) {
        return Object.assign({}, item, { state: "queued" });
      }
      const rows = this.task.dayResults || [];
      const row = rows.find((r) => r && r.date === date);
      if (row) {
        return Object.assign({}, item, { state: row.error ? "failed" : "done" });
      }
      if (item.state === "idle" || item.state === "paused") {
        return Object.assign({}, item, { state: "pending" });
      }
      return item;
    },

    recoverRunningTask() {
      this.recoverFromStore();
      getEvRunningSimulate()
        .then((res) => {
          const task = res && res.data;
          if (task && task.status === 0) {
            this.attachRunningTask(task);
            return;
          }
          if (!this.taskId) {
            this.clearStoredTaskId();
          }
        })
        .catch(() => { });
    },

    recoverFromStore() {
      const id = this.readStoredTaskId();
      if (!id) {
        return;
      }
      getEvSimulateTask(id).then((res) => {
        const task = res && res.data;
        if (task && task.status === 0) {
          this.attachRunningTask(task);
        } else {
          this.clearStoredTaskId();
        }
      });
    },

    onWheelSelectDate(date) {
      if (date) {
        this.viewDate = date;
      }
    },

    readStoredTaskId() {
      try {
        return localStorage.getItem(RUNNING_TASK_KEY);
      } catch (e) {
        return null;
      }
    },

    saveStoredTaskId(id) {
      try {
        if (id) {
          localStorage.setItem(RUNNING_TASK_KEY, id);
        } else {
          localStorage.removeItem(RUNNING_TASK_KEY);
        }
      } catch (e) { /* ignore */ }
    },

    clearStoredTaskId() {
      this.saveStoredTaskId(null);
    },

    attachRunningTask(task) {
      if (!task || !task.id) {
        return;
      }
      this.task = task;
      this.taskId = task.id;
      this.running = true;
      this.getResulted = true;
      this.saveStoredTaskId(task.id);
      if (task.startDate) {
        this.param.startDate = task.startDate;
      }
      if (task.days) {
        this.param.days = task.days;
      }
      this.startPoll();
    },

    startPoll() {
      this.stopPoll();
      this.pollTask();
      this.pollTimer = setInterval(() => this.pollTask(), 3000);
    },

    forceStopSimulate() {
      this.$confirm("将打断当前初始化并清掉闸门，之后可以重新点初始化。确认停止？", "强制停止", {
        type: "warning"
      }).then(() => {
        this.stopping = true;
        return forceStopEvSimulate();
      }).then(() => {
        this.running = false;
        this.stopping = false;
        this.getResulted = true;
        this.clearStoredTaskId();
        this.stopPoll();
        if (this.task) {
          this.task = Object.assign({}, this.task, {
            status: 2,
            errorMsg: "已强制停止",
            completeTime: this.task.completeTime || null,
            inFlightDates: []
          });
        }
        this.$message.success("已强制停止，可以重新初始化");
      }).catch((err) => {
        this.stopping = false;
        if (err === "cancel" || err === "close") {
          return;
        }
      });
    },

    startSimulate() {
      if (!this.param.startDate) {
        this.$message.error("请选择开始推导日期");
        return;
      }
      this.$confirm(
        `将按 ${this.param.startDate} 起共 ${this.param.days} 天重新初始化，确认继续？`,
        "初始化",
        { type: "warning" }
      ).then(() => {
        this.stopPoll();
        this.taskId = null;
        this.running = true;
        this.getResulted = false;
        this.task = {
          status: 0,
          currentDay: 0,
          totalDays: this.param.days || 0,
          startDate: this.param.startDate,
          days: this.param.days,
          inFlightDates: []
        };

        submitEvSimulate({
          startDate: this.param.startDate,
          days: this.param.days,
          _hideLoading: true
        })
          .then((res) => {
            this.getResulted = true;
            this.taskId = res.data.taskId;
            this.saveStoredTaskId(this.taskId);
            this.startPoll();
          })
          .catch(() => {
            this.running = false;
            this.getResulted = true;
            this.task = null;
            this.recoverRunningTask();
          });
      }).catch(() => { });
    },

    pollTask() {
      if (!this.taskId || !this.getResulted) return;

      getEvSimulateTask(this.taskId).then((res) => {
        this.task = res.data;
        if (this.task.status === 1) {
          this.running = false;
          this.clearStoredTaskId();
          this.stopPoll();
          this.viewCache.opAreaHour = {};
          this.viewCache.sellable = {};
          this.viewCache.vehicleFinal = {};
          this.viewCache.vehicleInit = {};
          this.viewCache.solveSummary = {};
          this.viewCache.plan = {};
          this.lastLoadedQueryKey = null;
          const sameDate = this.viewDate === this.param.startDate;
          this.viewDate = this.param.startDate;
          // 日期未变时 watcher 不会触发，需主动刷新；日期变了由 viewDate watcher 拉最新
          if (sameDate) {
            this.refreshAll();
          }
          this.$message.success("初始化完成，数据已刷新");
        } else if (this.task.status === 2) {
          this.running = false;
          this.clearStoredTaskId();
          this.stopPoll();
        }
      });
    },

    stopPoll() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },

    goConfig() {
      this.$router.push("/work_common/ev_simulate/config");
    },

    onMoreCommand(cmd) {
      if (cmd === "noop") {
        return;
      }
      if (this.$refs.moreDropdown && this.$refs.moreDropdown.hide) {
        this.$refs.moreDropdown.hide();
      }
      if (cmd === "opsTest") {
        this.openOpsTest();
        return;
      }
      if (cmd === "config") {
        this.goConfig();
      }
    },

    openOpsTest() {
      this.$refs.opsTestDialog.open();
    },

    onOpsTestAutoChange(on) {
      this.stopOpsTestAuto();
      if (!on) {
        this.$message.info("已关闭操作测试自动执行");
        return;
      }
      this.$message.success("已开启操作测试自动执行，每 3 分钟一次");
      this.runOpsTestOnce();
      this.opsTestAutoTimer = setInterval(() => this.runOpsTestOnce(), 3 * 60 * 1000);
    },

    stopOpsTestAuto() {
      if (this.opsTestAutoTimer) {
        clearInterval(this.opsTestAutoTimer);
        this.opsTestAutoTimer = null;
      }
    },

    runOpsTestOnce() {
      const dialog = this.$refs.opsTestDialog;
      if (dialog && typeof dialog.startIfIdle === "function") {
        dialog.startIfIdle();
      }
    },

    onOpsTestDone() {
      this.onRefresh();
    },

    refreshAfterPlanChange() {
      if (!this.viewDate) {
        return Promise.resolve();
      }
      this.$delete(this.viewCache.plan, this.viewDate);
      this.$delete(this.viewCache.vehicleFinal, this.viewDate);
      this.$delete(this.viewCache.solveSummary, this.viewDate);
      if (this.pathCacheKey) {
        this.$delete(this.viewCache.opAreaHour, this.pathCacheKey);
        this.$delete(this.viewCache.sellable, this.pathCacheKey);
      }
      this.lastLoadedQueryKey = null;
      return this.refreshAll();
    },

    formatAssignMsg(action, data) {
      const driverNo = data && data.driverNo ? data.driverNo : "-";
      const showTime = data && data.showTime ? data.showTime : "";
      return `${action}成功：司机 ${driverNo}${showTime ? " / " + showTime : ""}`;
    },

    handleAssignOrder(order) {
      if (!order || !order.id) {
        return;
      }
      this.$refs.assignDialog.open(order, false);
    },

    handleRevokeOrder(order) {
      if (!order || !order.id) {
        return;
      }
      this.$confirm("确认撤回该订单的规划绑定？", "撤回", {
        type: "warning"
      }).then(() => {
        return revokeEvOrder(order.id);
      }).then(() => {
        this.$message.success("已撤回规划绑定");
        return this.refreshAfterPlanChange();
      }).catch(() => { });
    },

    handleReassignOrder(order) {
      if (!order || !order.id) {
        return;
      }
      this.$refs.assignDialog.open(order, true);
    },

    onAssignSuccess({ action, data }) {
      this.$message.success(this.formatAssignMsg(action, data));
      this.refreshAfterPlanChange();
    }
  }
};
</script>

<style lang="less" scoped>
.ev-simulate-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  padding: 6px 12px 8px;
  box-sizing: border-box;
  overflow: hidden;
}

.page-header {
  flex-shrink: 0;
  margin-bottom: 6px;
  padding-bottom: 6px;
  border-bottom: 1px solid #ebeef5;

}

.header-main {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  min-height: 36px;
}

.header-block {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.block-label {
  font-size: 11px;
  color: #909399;
  writing-mode: vertical-rl;
  letter-spacing: 2px;
  margin-right: 8px;
  flex-shrink: 0;
  user-select: none;
}

.derive-toggle-wrap {
  order: 1;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.derive-toggle {
  padding-left: 0;
  padding-right: 4px;
  color: #606266;

  i {
    margin-right: 2px;
  }
}

.derive-block,
.view-block {
  padding: 4px 10px;
  background: #fafafa;
  border: 1px solid #ebeef5;
  border-radius: 4px;
}

.derive-block {
  order: 4;
  flex: 1 0 100%;
}

.view-block {
  order: 3;
}

.busy-conflict-tip {
  margin: 0 0 8px;
  color: #606266;
  font-size: 12px;
  line-height: 1.5;
}

.busy-conflict-paths {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}

.busy-conflict-detail {
  padding: 4px 8px;
  font-size: 12px;
  line-height: 1.6;
  color: #606266;
}

.busy-conflict-detail pre {
  margin: 6px 0 0;
  white-space: pre-wrap;
  word-break: break-all;
  color: #909399;
}

.more-btn.is-auto {
  position: relative;
}

.more-btn.is-auto::after {
  content: "";
  position: absolute;
  top: 3px;
  right: 3px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #67c23a;
}

.ops-auto-item {
  display: flex !important;
  align-items: center;
  justify-content: space-between;
  min-width: 148px;
}

.ev-block-wrap {
  order: 2;
  flex: 1;
  min-width: 0;
  display: flex;

  /deep/ .el-tooltip {
    flex: 1;
    min-width: 0;
    display: flex;
    width: 100%;
  }
}

.ev-clear-result {
  flex: none;
  margin-left: 2px;
  padding: 0 4px;
  color: #909399;
}

.ev-block {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex: 1;
  width: 100%;
  gap: 4px;
  padding: 4px 10px;
  background: linear-gradient(135deg, #f0f7ff 0%, #fafcff 100%);
  border: 1px solid #d9ecff;
  border-radius: 4px;
  min-width: 0;
  flex-wrap: nowrap;
  overflow-x: auto;
  white-space: nowrap;
  box-sizing: border-box;

  /* 宽度够时居中；超出时从左侧排开，避免左侧被裁掉且无法滚回 */
  &::before,
  &::after {
    content: "";
    margin: auto;
  }

  &::-webkit-scrollbar {
    height: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c0c4cc;
    border-radius: 2px;
  }
}

.ev-placeholder {
  background: #fafafa;
  border: 1px dashed #dcdfe6;
}

.ev-placeholder-text {
  font-size: 12px;
  color: #c0c4cc;
  width: 100%;
  text-align: center;
}

.ev-metric {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  padding: 0 6px;
  border-right: 1px solid #e4e7ed;

  &:last-child {
    border-right: none;
  }
}

.ev-metric-label {
  font-size: 11px;
  color: #909399;
  white-space: nowrap;
  line-height: 1.4;
}

.ev-metric.is-cross-slot .ev-metric-value {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
}

.ev-cross-slot-tag {
  white-space: nowrap;
  line-height: 1.4;
  cursor: pointer;
}

.ev-cross-slot-tag.is-open {
  color: #e6a23c;
}

.ev-cross-slot-tag.is-enough {
  color: #67c23a;
}

.ev-cross-slot-menu-item {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 6px 4px;
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: #f5f7fa;
  }

  .is-enough {
    color: #67c23a;
  }
}

.ev-metric-value {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  font-variant-numeric: tabular-nums;
  line-height: 1.4;

  &.muted {
    color: #606266;
    font-size: 13px;
  }
}

.ev-metric.is-penalty .ev-metric-value {
  color: #f56c6c;
}

.ev-metric.is-clickable {
  cursor: pointer;
}

.ev-metric.is-clickable .ev-metric-value {
  text-decoration: underline;
}

.slack-detail-tip {
  margin: 0 0 10px;
  color: #606266;
  font-size: 13px;
  line-height: 1.5;
}

.ev-metric.is-objective .ev-metric-value {
  color: #409eff;
  font-size: 14px;
}

.ev-metric.is-objective.is-mismatch .ev-metric-value {
  color: #f56c6c;
  text-decoration: underline;
}

.ev-metric.is-reconcile .ev-metric-value {
  color: #e6a23c;
}

.ev-metric.is-unassigned .ev-metric-value {
  color: #f56c6c;
}

.ev-metric.is-warn .ev-metric-value {
  color: #e6a23c;
}

.ev-metric.is-ok .ev-metric-value {
  color: #67c23a;
}

.ev-metric-unit {
  font-size: 11px;
  font-weight: 500;
}

.ev-metric-gap {
  margin-left: 4px;
  font-size: 11px;
  font-weight: 500;
  color: #909399;
}

.task-status {
  display: flex;
  align-items: center;
}

.status-text {
  font-size: 12px;
  color: #606266;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 280px;
}

.toolbar__done {
  font-size: 12px;
  color: #67c23a;
  white-space: nowrap;
}

.toolbar__error {
  font-size: 12px;
  color: #f56c6c;
  max-width: 420px;
  white-space: normal;
  word-break: break-all;
  line-height: 1.4;
  cursor: pointer;
}

.compact-form {
  /deep/ .el-form-item {
    margin-bottom: 0;
    margin-right: 8px;
  }

  /deep/ .el-form-item__label {
    padding-right: 4px;
    font-size: 12px;
  }
}

.derive-block,
.view-block {
  /deep/ .el-form {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
  }
}

.main-content {
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.tabs-toolbar {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  height: 40px;
}

.tel-search {
  width: 180px;
}

.fill-tabs {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;

  /deep/ .el-tabs__header {
    margin-bottom: 6px;
    padding-right: 190px;
  }

  /deep/ .el-tabs__content {
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }

  /deep/ .el-tab-pane {
    height: 100%;
    overflow: hidden;
  }

  /* 覆盖 global.less：.root-box padding 10px、.page margin-top 10px，避免把汇总行挤出可视区 */
  /deep/ .root-box {
    height: 100%;
    padding: 0 0 10px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
  }

  /deep/ .root-box>.page {
    flex-shrink: 0;
    margin-top: 0;
    box-sizing: border-box;
    height: 28px;
    padding-top: 4px;
    line-height: 20px;
  }
}

.idle-empty-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #909399;
  font-size: 13px;
}
</style>
